import React from "react";
import PropTypes from "prop-types";
import { Label, Dropdown, InputText } from "../../../../components/Inputs";
import {
  JarvisConnectionPermissionOptions,
  SiSTelekomPermission,
  getJarvisPermissionLabel,
  normalizeJarvisPermission,
} from "../Utils";
import { validateEmail } from "../../../../components/Utils";

const getEmptyModel = () => ({
  parent_company_id: 0,
  parentCompanyName: "",
  permissions: [],
  externalEmail: "",
});

function generateState(
  pModel,
  kdlCompanyList = [],
  defaultExternalEmail = "",
  readOnly = false,
) {
  const model = pModel || {};
  let permissions = "";
  if (model.permissions) {
    const tokens = String(model.permissions).split(/\s*,\s*/);
    permissions = getJarvisPermissionLabel(tokens[0]);
  }
  // SiS Telekom Herbeiruf is always active together with the SiS permission,
  // so the external e-mail is pre-filled with the default one when empty
  const isSisTelekom =
    normalizeJarvisPermission(permissions) ===
    normalizeJarvisPermission(SiSTelekomPermission);
  const externalEmail =
    model.externalEmail ||
    (isSisTelekom && !readOnly ? defaultExternalEmail : "") ||
    "";
  return {
    parent_company_id: model.parent_company_id || 0,
    parentCompanyName: kdlCompanyList.length > 0 ? kdlCompanyList[0].name : "",
    permissions,
    externalEmail,
    error: {
      parent_company_id: null,
      permissions: null,
    },
  };
}

function JarvisConnectionForm({
  forwardedRef,
  className,
  kdlCompanyList,
  defaultExternalEmail,
  readOnly,
  isDL,
  model,
}) {
  const [state, setState] = React.useState(() =>
    generateState(model, kdlCompanyList, defaultExternalEmail, readOnly),
  );

  React.useEffect(() => {
    setState(
      generateState(model, kdlCompanyList, defaultExternalEmail, readOnly),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, kdlCompanyList, defaultExternalEmail, readOnly]);

  const validate = React.useCallback(
    (initialize) => {
      const { error, ...modelState } = state;
      let isValid = true;
      const newError = { ...error };
      Object.keys(modelState).forEach((key) => {
        // externalEmail is validated below, as it is only required for the
        // SiS permission
        if (key === "externalEmail") {
          return;
        }
        if (newError[key] !== undefined) {
          if (key === "permissions") {
            if (!modelState[key] || modelState[key].length === 0) {
              newError[key] = true;
              isValid = false;
            }
          } else if (modelState[key] === "") {
            newError[key] = true;
            isValid = false;
          }
        }
      });
      // SiS Telekom Herbeiruf is always active together with the SiS
      // permission, so the external e-mail is mandatory in that case
      const isSisTelekom =
        normalizeJarvisPermission(modelState.permissions) ===
        normalizeJarvisPermission(SiSTelekomPermission);
      if (isSisTelekom) {
        if (
          !modelState.externalEmail ||
          modelState.externalEmail.trim() === "" ||
          !validateEmail(modelState.externalEmail)
        ) {
          newError.externalEmail = true;
          isValid = false;
        } else {
          newError.externalEmail = null;
        }
      } else {
        newError.externalEmail = null;
      }
      setState((prev) => ({ ...prev, error: newError }));
      if (!isValid) {
        return null;
      }
      if (initialize) {
        setState(getEmptyModel());
      }
      const { id, index, status, status_id } = model;
      return {
        ...modelState,
        permissions: getJarvisPermissionLabel(modelState.permissions),
        // the flag is derived from the permission: SiS Telekom Herbeiruf is
        // implicitly enabled for the SiS permission and disabled otherwise
        isSISTelekomHerbeiruf: isSisTelekom ? 1 : 0,
        // the external e-mail is only used together with SiS Telekom Herbeiruf
        externalEmail: isSisTelekom ? modelState.externalEmail : "",
        id,
        index,
        status,
        status_id,
        oldStatusId: status_id,
      };
    },
    [state, model],
  );

  const getModel = React.useCallback(
    (initialize = false) => {
      return validate(initialize);
    },
    [validate],
  );

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      getModel,
    }),
    [getModel],
  );

  const setPermissions = React.useCallback(
    (permissions) => {
      setState((prev) => ({
        ...prev,
        permissions: getJarvisPermissionLabel(permissions),
        // SiS Telekom Herbeiruf is implicitly active for the SiS permission,
        // so the external e-mail is pre-filled with the default one
        externalEmail:
          normalizeJarvisPermission(permissions) ===
            normalizeJarvisPermission(SiSTelekomPermission) &&
          !readOnly &&
          prev.externalEmail === ""
            ? defaultExternalEmail || ""
            : prev.externalEmail,
        error: {
          ...prev.error,
          permissions: permissions === "",
        },
      }));
    },
    [defaultExternalEmail, readOnly],
  );

  const onChangeExternalEmail = React.useCallback((email) => {
    setState((prev) => ({
      ...prev,
      externalEmail: email,
    }));
  }, []);

  const { parentCompanyName, permissions, externalEmail, error } = state;
  const KDLCompanyOptions = kdlCompanyList.map((item) => ({
    label: item.name,
    value: `${item.id}`,
  }));

  const permissionOptions = React.useMemo(
    () =>
      JarvisConnectionPermissionOptions.filter((option) => {
        if (isDL) {
          return normalizeJarvisPermission(option.value) === "NE4";
        }
        return true;
      }),
    [isDL],
  );

  return (
    <div className={className} style={{ minHeight: "288px" }}>
      <div
        className={`form-group-item ${
          error.parentCompanyName ? "error-group" : ""
        }`}
      >
        <Label htmlFor="parentCompanyName">{`Vertragspartner*`}</Label>
        <Dropdown
          id="parentCompanyName"
          className="w3-block w3-border"
          value={parentCompanyName}
          placeholder="Bitte auswählen"
          options={KDLCompanyOptions}
          readOnly
        />
      </div>
      <div
        className={`form-group-item ${error.permissions ? "error-group" : ""}`}
      >
        <Label htmlFor="permissions">{`Rechte*`}</Label>
        <Dropdown
          id="permissions"
          className="w3-block w3-border"
          value={permissions}
          placeholder="Bitte auswählen"
          options={permissionOptions}
          onChange={(event) => setPermissions(event.value)}
          readOnly={readOnly}
        />
      </div>
      {normalizeJarvisPermission(permissions) ===
        normalizeJarvisPermission(SiSTelekomPermission) && (
        <div
          className={`form-group-item margin-top ${
            error.externalEmail ? "error-group" : ""
          }`}
        >
          <Label htmlFor="externalEmail">{`Externe E-Mail-Adresse*`}</Label>
          <InputText
            id="externalEmail"
            className={`w3-block w3-border${readOnly ? " w3-round pad" : ""}`}
            value={externalEmail}
            onChange={(event) => onChangeExternalEmail(event.target.value)}
            readonly={readOnly}
          />
          <div className="pad-tb w3-medium w3-text-grey">
            {"Wird für die Kommunikation mit der Telekom genutzt"}
          </div>
        </div>
      )}
    </div>
  );
}

JarvisConnectionForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    parent_company_id: PropTypes.number,
    permissions: PropTypes.string,
    externalEmail: PropTypes.string,
  }),
  kdlCompanyList: PropTypes.array,
  // isContractor: PropTypes.bool.isRequired,
  defaultExternalEmail: PropTypes.string,
  readOnly: PropTypes.bool,
  isDL: PropTypes.bool,
};

JarvisConnectionForm.defaultProps = {
  className: "",
  model: {
    parent_company_id: 0,
    permissions: "",
    externalEmail: "",
  },
  companyList: [],
  kdlCompanyList: [],
  defaultExternalEmail: "",
  isDL: false,
};

export default React.forwardRef((props, ref) => {
  return <JarvisConnectionForm {...props} forwardedRef={ref} />;
});
